import { Target, MoonStars, Medal, CheckCircle, Warning, WarningCircle, Info } from "@phosphor-icons/react";

const levelIcon = {
  success: <CheckCircle size={22} weight="fill" className="text-[#00e676]" />,
  warning: <Warning size={22} weight="fill" className="text-[#ffea00]" />,
  alert: <WarningCircle size={22} weight="fill" className="text-[#ff3b30]" />,
  info: <Info size={22} weight="fill" className="text-primary" />,
};
const statusColor = { under: "text-primary", optimal: "text-[#00e676]", over: "text-[#ff3b30]" };

export function StrainCoachCard({ strain, target, verdict }) {
  if (!target) return null;
  const pct = (v) => `${Math.min((v / 21) * 100, 100)}%`;
  return (
    <div className="glass-card p-6" data-testid="strain-coach-card">
      <div className="flex items-center gap-2 mb-1">
        <Target size={18} weight="duotone" className="text-primary" />
        <h3 className="font-heading font-semibold">Цель по нагрузке</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">{target.label} · {target.min}–{target.max}</p>
      <div className="relative h-3 bg-secondary rounded-full mb-2">
        <div className="absolute h-full bg-primary/25 rounded-full"
          style={{ left: pct(target.min), width: `calc(${pct(target.max)} - ${pct(target.min)})` }} />
        {strain != null && (
          <div className="absolute -top-0.5 w-1 h-4 rounded bg-white" style={{ left: pct(strain) }} data-testid="strain-marker" />
        )}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground num mb-3"><span>0</span><span>21</span></div>
      {strain != null && <div className="text-sm mb-1">Текущая нагрузка: <span className="num font-semibold">{strain}</span></div>}
      {verdict && <p className={`text-xs leading-relaxed ${statusColor[verdict.status] || "text-muted-foreground"}`}>{verdict.text}</p>}
      <p className="text-xs text-muted-foreground mt-2">{target.text}</p>
    </div>
  );
}

export function SleepCoachCard({ sleep }) {
  return (
    <div className="glass-card p-6" data-testid="sleep-coach-card">
      <div className="flex items-center gap-2 mb-1">
        <MoonStars size={18} weight="duotone" className="text-[#a970ff]" />
        <h3 className="font-heading font-semibold">Сон сегодня</h3>
      </div>
      {!sleep ? (
        <p className="text-sm text-muted-foreground mt-3">Недостаточно данных о сне</p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-4">Рекомендация на предстоящую ночь</p>
          <div className="font-heading text-4xl font-bold num text-[#a970ff]" data-testid="sleep-recommended">{sleep.recommended_h} ч</div>
          <div className="mt-4 space-y-1.5 text-xs">
            <div className="flex justify-between"><span className="text-muted-foreground">Базовая потребность</span><span className="num">{sleep.baseline_h} ч</span></div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Долг сна</span>
              <span className={`num ${sleep.debt_extra_h >= 1 ? "text-[#ff3b30] font-semibold" : ""}`}>+{sleep.debt_extra_h} ч</span>
            </div>
            <div className="flex justify-between"><span className="text-muted-foreground">За дневную нагрузку</span><span className="num">+{sleep.strain_extra_h} ч</span></div>
            {sleep.last_sleep_h != null && (
              <div className="flex justify-between pt-1.5 border-t border-border/40"><span className="text-muted-foreground">Прошлой ночью</span><span className="num">{sleep.last_sleep_h} ч</span></div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function VerdictCard({ verdict }) {
  if (!verdict) return null;
  return (
    <div className="glass-card p-6" data-testid="verdict-card">
      <div className="flex items-center gap-2 mb-1">
        <Medal size={18} weight="duotone" className="text-[#ffea00]" />
        <h3 className="font-heading font-semibold">Вердикт недели</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Баланс нагрузки и восстановления за 7 дней</p>
      <div className="flex items-center gap-2 mb-3">
        {levelIcon[verdict.level]}
        <span className="font-heading text-lg font-bold" data-testid="verdict-state">{verdict.state}</span>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{verdict.text}</p>
    </div>
  );
}

export function HealthMonitorCard({ monitor, onSelect }) {
  if (!monitor?.length) return null;
  const dot = { norm: "bg-[#00e676]", high: "bg-[#ff3b30]", low: "bg-[#ffea00]" };
  const statusLabel = { norm: "В норме", high: "Выше нормы", low: "Ниже нормы" };
  return (
    <div className="glass-card p-6" data-testid="health-monitor-card">
      <h3 className="font-heading font-semibold mb-1">Монитор здоровья</h3>
      <p className="text-xs text-muted-foreground mb-4">Ваши персональные нормы рассчитаны по данным за период · нажмите на показатель для деталей</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {monitor.map((m) => (
          <button key={m.key} onClick={() => onSelect?.(m.key)} data-testid={`monitor-${m.key}`}
            className="border-l-2 border-border pl-3 text-left rounded-r-md hover:bg-secondary/50 transition-colors py-1 -my-1">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{m.label}</div>
            <div className="font-heading text-xl font-bold num">{m.current} <span className="text-xs font-normal text-muted-foreground">{m.unit}</span></div>
            <div className="text-[11px] text-muted-foreground num">норма {m.min}–{m.max}</div>
            <div className="flex items-center gap-1.5 mt-1">
              <span className={`w-2 h-2 rounded-full ${dot[m.status]}`} />
              <span className="text-[11px]">{statusLabel[m.status]}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
